import { SearchParamsChange, SetSearchParams } from 'interfaces/SearchParams';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { services } from 'services/container';
import { SearchParamsHelper } from 'utils/searchParamsHelper';

export const useSearchParams = (
    defaultInit?: SearchParamsChange,
): [URLSearchParams, SetSearchParams] => {
    const defaultSearchParamsRef = useRef(SearchParamsHelper.createSearchParams(defaultInit));
    const didSetSearchParamsRef = useRef(false);
    const [dependency, reinvokeSearchParams] = useReducer((x) => x + 1, 0);

    const handleForceUpdateSearchParamsDependency = useCallback(reinvokeSearchParams, []);

    useEffect(() => {
        const unsubscribe = services.widget.eventChannel.on(
            'forceUpdateSearchParamsDependency',
            handleForceUpdateSearchParamsDependency,
        );

        return () => {
            unsubscribe();
        };
    }, []);

    const searchParams = useMemo(
        () =>
            SearchParamsHelper.getSearchParamsForLocation(
                window.location.search,
                didSetSearchParamsRef.current ? null : defaultSearchParamsRef.current,
            ),
        [dependency, window.location.href, didSetSearchParamsRef.current],
    );

    const setSearchParams = useCallback<SetSearchParams>(
        (change) => {
            const newSearchParams = SearchParamsHelper.createSearchParams(change(searchParams));

            didSetSearchParamsRef.current = true;

            const path = [
                window.location.origin,
                window.location.pathname,
                '?',
                String(newSearchParams),
            ].join('');

            window.history.pushState(null, null, path);
            reinvokeSearchParams();
        },
        [searchParams],
    );

    return [searchParams, setSearchParams];
};
